import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { runLlmGenerationTask } from "@/llm-generation/client-runtime"
import { LLM_GENERATION_TASK_IDS } from "@/llm-generation/protocol"
import { useGetOrgRecordTransport } from "@/organization"
import { useTestSetMetadataKeysTransport } from "@/test-set/transport"
import { useUser } from "@clerk/nextjs"
import { Button, Tooltip } from "@coval/ui"
import { Check, GitMerge, Info, List, X } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { InlineDiff } from "@/components/diff/InlineDiff"
import type { Annotation } from "@/types/api/Annotation"
import type { MetricRecordData } from "@/types/metrics/contracts"

import { METRIC_VIEWER_EVENTS, metricViewerEventBus } from "../../editor-events"
import { ChangeCard } from "../optimization/ChangeCard"
import type { MetricManager } from "../optimization/constants"
import type {
  ImprovementConfig,
  OptimizationResult,
} from "../optimization/optimization"
import {
  parseOptimizationResponse,
  supportsOptimization,
} from "../optimization/optimizationPrompts"
import { ContextPreviewModal } from "../prompt-improvement/ContextPreviewModal"
import { buildEnhancedFeedbackContext } from "../prompt-improvement/feedbackAnalysis"
import { usePromptFeedbackData } from "../prompt-improvement/usePromptFeedbackData"
import { useUploadedConversationMetadataTransport } from "../routes/useMetricConfigurationTransport"
import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_STYLES,
  TEXTAREA_CONTAINER_STYLES,
  TEXTAREA_STYLES,
} from "../styles"
import { MetricPromptMentionInput } from "./MetricPromptMentionInput"
import { displayToStored, storedToDisplay } from "./metricPromptMentionTokens"
import { buildQuestionTextareaMentionSections } from "./questionTextareaMentionSections"
import { RequiredMarker } from "./RequiredMarker"
import { usePromptOptimizationSelection } from "./usePromptOptimizationSelection"

const log = logger("QuestionTextarea")

const MAX_PROMPT_CHARACTERS = 20000
const METADATA_QUERY_STALE_TIME_MS = 5 * 60 * 1000

const PROMPT_ANIMATION_SEEN_PREFIX = "coval:metricPromptAnimated:"

// Stable short key for a prompt so the template streaming reveal plays only
// once per prompt and never replays on reload. Deliberately NOT org-scoped:
// it's a content hash of the prompt text itself, so a cross-org hash
// collision only means the (harmless, cosmetic) reveal animation is skipped
// once — it never exposes another org's prompt content.
const promptAnimationKey = (prompt: string): string => {
  let h = 0
  for (let i = 0; i < prompt.length; i++) {
    h = (Math.imul(31, h) + prompt.charCodeAt(i)) | 0
  }
  return `${PROMPT_ANIMATION_SEEN_PREFIX}${(h >>> 0).toString(36)}`
}

const hasSeenPromptAnimation = (prompt: string): boolean => {
  if (typeof window === "undefined") {
    return false
  }
  try {
    return window.localStorage.getItem(promptAnimationKey(prompt)) !== null
  } catch {
    return false
  }
}

const markPromptAnimationSeen = (prompt: string): void => {
  if (typeof window === "undefined") {
    return
  }
  try {
    window.localStorage.setItem(promptAnimationKey(prompt), "1")
  } catch {
    // Ignore storage failures (private mode / quota); animation just replays.
  }
}

interface QuestionTextareaProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  label?: string
  placeholder?: string
  textAreaRef?: React.RefObject<HTMLTextAreaElement>
  /** Optional test set id used to populate the @-mention picker's test case metadata section. */
  testSetId?: string
  /** Appends a required marker to the label; the prompt is a required field for LLM judges. */
  required?: boolean
}

export const QuestionTextarea: React.FC<QuestionTextareaProps> = ({
  metricRecordData,
  onChange,
  label = "Metric Prompt",
  placeholder = "Enter a question to evaluate your transcript",
  textAreaRef,
  testSetId,
  required = false,
}) => {
  const { trackEvent } = useAnalytics()
  const { data: organizationRecord } = useGetOrgRecordTransport()
  const { user } = useUser()

  const [showContextPreview, setShowContextPreview] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [optimizationResult, setOptimizationResult] =
    useState<OptimizationResult | null>(null)
  const [optimizationError, setOptimizationError] = useState<string | null>(
    null,
  )

  const currentPrompt = metricRecordData.metric_metadata?.question || ""
  const metricType = metricRecordData.metric_manager
  const metricId = metricRecordData.id
  const {
    acceptedIds,
    clearSelection,
    combinedPreview,
    selectAll,
    setViewMode,
    toggleChange,
    viewMode,
  } = usePromptOptimizationSelection({
    currentPrompt,
    optimizationResult,
  })

  // Streaming animation for template-created metrics (new metric with pre-filled prompt)
  const [isAnimating, setIsAnimating] = useState(
    () =>
      !metricId &&
      currentPrompt.length > 0 &&
      !hasSeenPromptAnimation(currentPrompt),
  )
  const [visibleText, setVisibleText] = useState("")

  useEffect(() => {
    if (!isAnimating) {
      return
    }

    const words = currentPrompt.split(/(\s+)/).filter((w) => w.length > 0)
    let wordIndex = 0
    let lastTime = 0
    let nextDelay = 500
    let frameId: number

    const tick = (timestamp: number) => {
      if (!lastTime) {
        lastTime = timestamp
      }
      if (timestamp - lastTime < nextDelay) {
        frameId = requestAnimationFrame(tick)
        return
      }
      lastTime = timestamp

      wordIndex += Math.min(
        1 + Math.floor(Math.random() * 2),
        words.length - wordIndex,
      )
      setVisibleText(words.slice(0, wordIndex).join(""))

      if (wordIndex >= words.length) {
        setIsAnimating(false)
        return
      }

      const lastWord = words[wordIndex - 1]
      if (lastWord?.includes("\n")) {
        nextDelay = 60 + Math.random() * 80
      } else if (/[.!?]$/.test(lastWord ?? "")) {
        nextDelay = 40 + Math.random() * 50
      } else {
        nextDelay = 15 + Math.random() * 30
      }

      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [isAnimating, currentPrompt])

  // Let users skip the streaming reveal by clicking/focusing the prompt box.
  const focusAfterSkipRef = useRef(false)
  const handleSkipAnimation = useCallback(() => {
    if (!isAnimating) {
      return
    }
    focusAfterSkipRef.current = true
    setIsAnimating(false)
  }, [isAnimating])

  useEffect(() => {
    if (!isAnimating && focusAfterSkipRef.current) {
      focusAfterSkipRef.current = false
      textAreaRef?.current?.focus()
    }
  }, [isAnimating, textAreaRef])

  // Mark the reveal as seen once it starts so it never replays (e.g. on reload).
  useEffect(() => {
    if (isAnimating) {
      markPromptAnimationSeen(currentPrompt)
    }
  }, [isAnimating, currentPrompt])

  const characterCount = currentPrompt.length
  const isOverLimit = characterCount > MAX_PROMPT_CHARACTERS

  const {
    allAnnotations,
    feedbackUnavailableReason,
    isFeedbackDataLoading,
    metricOutputs,
    relatedAnnotations,
  } = usePromptFeedbackData(metricId)

  // The stored prompt is canonical `{{ section.key }}` template syntax. The
  // mention input renders `@section.key` chips instead — convert on the fly
  // so users see `@`, parent/server always sees `{{...}}`.
  const displayPrompt = useMemo(
    () => storedToDisplay(currentPrompt),
    [currentPrompt],
  )

  const handleDisplayPromptChange = useCallback(
    (nextDisplay: string) => {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          question: displayToStored(nextDisplay),
        },
      })
    },
    [metricRecordData, onChange],
  )

  const { data: testSetMetadataKeys = [] } = useTestSetMetadataKeysTransport(
    { testSetId },
    { staleTime: METADATA_QUERY_STALE_TIME_MS },
  )

  // Customer metadata keys the org has actually uploaded (via
  // POST /v1/conversations:submit `metadata`, stored as run.customer_metadata).
  // Lets users discover + insert {{customer_metadata.<key>}} in the picker, the
  // same way test_case attributes are surfaced. Backed by the template-variable
  // schema's customer_metadata category (source_field: run.customer_metadata).
  const { data: uploadedConversationMetadata } =
    useUploadedConversationMetadataTransport({
      staleTime: METADATA_QUERY_STALE_TIME_MS,
    })

  const mentionSections = useMemo(
    () =>
      buildQuestionTextareaMentionSections({
        testSetMetadataKeys,
        customerMetadataKeys: uploadedConversationMetadata?.all_keys,
      }),
    [testSetMetadataKeys, uploadedConversationMetadata?.all_keys],
  )

  const handleContextConfirm = useCallback(
    async (config: ImprovementConfig) => {
      try {
        setIsGenerating(true)
        setOptimizationError(null)

        // Emit event for external listeners
        metricViewerEventBus.emit(
          METRIC_VIEWER_EVENTS.PROMPT_OPTIMIZE_LOADING,
          {
            isLoading: true,
          },
        )

        const selectedAnnotations = relatedAnnotations.filter((a: Annotation) =>
          config.selectedAnnotationIds.includes(a.id),
        )

        let feedbackContext: string | undefined
        if (
          config.improvementFocus !== "general" &&
          selectedAnnotations.length > 0
        ) {
          feedbackContext = buildEnhancedFeedbackContext({
            annotations: selectedAnnotations,
            metricOutputs,
            metric: metricRecordData,
            currentUserId: user?.id,
          })
        }

        const improvedResponse = await runLlmGenerationTask<string>({
          taskId: LLM_GENERATION_TASK_IDS.METRIC_PROMPT_OPTIMIZE,
          input: {
            metricType,
            currentPrompt,
            improvementFocus: config.improvementFocus,
            ...(feedbackContext ? { feedbackContext } : {}),
          },
        })

        if (!improvedResponse) {
          throw new Error("No response received from optimization service")
        }

        const result = parseOptimizationResponse(
          improvedResponse,
          currentPrompt,
        )
        setOptimizationResult(result)
        setOptimizationError(null)
        setShowContextPreview(false)

        // Emit event for external listeners
        metricViewerEventBus.emit(
          METRIC_VIEWER_EVENTS.PROMPT_OPTIMIZE_COMPLETE,
          {
            originalPrompt: currentPrompt,
            improvedPrompt: result.improvedMetric,
            explanation: result.explanation,
          },
        )

        trackEvent(TRACKING_EVENTS.HUMAN_REVIEW.EXPERIMENT_RUN, {
          experiment_type: `improve_prompt_${config.improvementFocus}`,
          mode: "metric",
          organization_id: organizationRecord?.id,
          annotations_used: selectedAnnotations.length,
          timestamp: new Date().toISOString(),
        })
      } catch (error) {
        log.error("Failed to improve prompt", error)
        setOptimizationError(
          error instanceof Error
            ? error.message
            : "Failed to improve prompt. Please try again.",
        )
      } finally {
        metricViewerEventBus.emit(
          METRIC_VIEWER_EVENTS.PROMPT_OPTIMIZE_LOADING,
          {
            isLoading: false,
          },
        )
        setIsGenerating(false)
      }
    },
    [
      currentPrompt,
      metricType,
      metricOutputs,
      metricRecordData,
      relatedAnnotations,
      user?.id,
      trackEvent,
      organizationRecord?.id,
    ],
  )

  const handleContextPreviewClose = useCallback(() => {
    setOptimizationError(null)
    setShowContextPreview(false)
  }, [])

  const handleImproveClick = useCallback(() => {
    if (!currentPrompt || currentPrompt.trim() === "") {
      return
    }

    if (!supportsOptimization(metricType as MetricManager)) {
      return
    }

    setOptimizationError(null)
    setShowContextPreview(true)
  }, [currentPrompt, metricType])

  const handleAcceptChanges = useCallback(() => {
    if (!optimizationResult || acceptedIds.size === 0) {
      return
    }

    const acceptedChanges = optimizationResult.changes.filter((c) =>
      acceptedIds.has(c.id),
    )
    const finalPrompt = combinedPreview

    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        question: finalPrompt,
      },
    })

    metricViewerEventBus.emit(METRIC_VIEWER_EVENTS.PROMPT_OPTIMIZE_ACCEPT, {
      improvedPrompt: finalPrompt,
    })

    trackEvent(TRACKING_EVENTS.HUMAN_REVIEW.EXPERIMENT_RUN, {
      experiment_type: "improve_prompt_accepted",
      mode: "metric",
      organization_id: organizationRecord?.id,
      changes_accepted: acceptedChanges.length,
      change_types: acceptedChanges.map((c) => c.type),
      timestamp: new Date().toISOString(),
    })

    setOptimizationResult(null)
    clearSelection()
  }, [
    metricRecordData,
    onChange,
    trackEvent,
    organizationRecord?.id,
    optimizationResult,
    acceptedIds,
    combinedPreview,
    clearSelection,
  ])

  const handleRejectChanges = useCallback(() => {
    metricViewerEventBus.emit(METRIC_VIEWER_EVENTS.PROMPT_OPTIMIZE_REJECT, {
      originalPrompt: currentPrompt,
    })

    trackEvent(TRACKING_EVENTS.HUMAN_REVIEW.EXPERIMENT_RUN, {
      experiment_type: "improve_prompt_rejected",
      mode: "metric",
      organization_id: organizationRecord?.id,
      timestamp: new Date().toISOString(),
    })

    setOptimizationResult(null)
    clearSelection()
  }, [clearSelection, currentPrompt, trackEvent, organizationRecord?.id])

  const isDisabled =
    !currentPrompt ||
    currentPrompt.trim() === "" ||
    isGenerating ||
    isFeedbackDataLoading ||
    !supportsOptimization(metricType as MetricManager)

  return (
    <>
      <div className={FIELD_CONTAINER_STYLES}>
        {/* Header with label and improve button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <label className={LABEL_STYLES}>
              {label}
              {required && <RequiredMarker />}
            </label>
            <Tooltip
              content="The question that will be used to evaluate the transcript"
              side="top"
            >
              <button
                type="button"
                className="inline-flex items-center"
                aria-label="Evaluation question info"
              >
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              data-metric-editor-action
              variant="secondary"
              disabled={isDisabled}
              onClick={handleImproveClick}
            >
              {isGenerating
                ? "Optimizing..."
                : isFeedbackDataLoading
                  ? "Loading feedback..."
                  : "Optimize Metric"}
            </Button>
            <Button
              data-metric-editor-action
              variant="secondary"
              disabled={!currentPrompt || currentPrompt.trim() === ""}
              onClick={() => {
                trackEvent(TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED, {
                  metric_id: metricId,
                })
                metricViewerEventBus.emit(METRIC_VIEWER_EVENTS.TEST_METRIC_OPEN)
              }}
            >
              Test Metric
            </Button>
          </div>
        </div>

        {/* Textarea or Inline Diff */}
        <div
          className={`${TEXTAREA_CONTAINER_STYLES} ${
            isOverLimit ? "ring-2 ring-coval-danger-border" : ""
          }`}
        >
          {optimizationResult ? (
            <div className="flex flex-col min-h-[300px]">
              {/* Header row with tabs and selection controls */}
              <div className="flex items-center justify-between gap-2 pb-2 border-b border-coval-border shrink-0">
                <div className="flex" role="tablist">
                  <button
                    onClick={() => setViewMode("list")}
                    role="tab"
                    aria-selected={viewMode === "list"}
                    className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-l border transition-colors ${
                      viewMode === "list"
                        ? "bg-surface-default text-coval-text shadow-sm border-coval-border-active"
                        : "border-coval-border text-coval-text-muted hover:text-coval-text"
                    }`}
                  >
                    <List className="w-3 h-3" />
                    List
                  </button>
                  <button
                    onClick={() => setViewMode("combined")}
                    role="tab"
                    aria-selected={viewMode === "combined"}
                    className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-r border-y border-r transition-colors ${
                      viewMode === "combined"
                        ? "bg-surface-default text-coval-text shadow-sm border-coval-border-active"
                        : "border-coval-border text-coval-text-muted hover:text-coval-text"
                    }`}
                  >
                    <GitMerge className="w-3 h-3" />
                    Combined
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-coval-text-muted">
                    {acceptedIds.size}/{optimizationResult.changes.length}
                  </span>
                  <Button
                    variant="ghost"
                    className="text-xs h-6 min-w-0 px-2"
                    onClick={
                      acceptedIds.size === optimizationResult.changes.length
                        ? clearSelection
                        : selectAll
                    }
                  >
                    {acceptedIds.size === optimizationResult.changes.length
                      ? "None"
                      : "All"}
                  </Button>
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto py-2 min-h-0">
                {viewMode === "list" ? (
                  <div className="space-y-2">
                    {optimizationResult.changes.map((change, index) => (
                      <ChangeCard
                        key={change.id}
                        change={change}
                        isSelected={acceptedIds.has(change.id)}
                        onToggleSelect={() => toggleChange(change.id)}
                        index={index}
                        total={optimizationResult.changes.length}
                      />
                    ))}
                  </div>
                ) : (
                  <InlineDiff
                    oldText={currentPrompt}
                    newText={combinedPreview}
                  />
                )}
              </div>

              {/* Footer with accept/reject buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-coval-border shrink-0">
                <Button
                  variant="danger"
                  className="h-7"
                  onClick={handleRejectChanges}
                >
                  <X className="w-3 h-3" />
                  Reject
                </Button>
                <Button
                  className="h-7"
                  onClick={handleAcceptChanges}
                  disabled={acceptedIds.size === 0}
                >
                  <Check className="w-3 h-3" />
                  Accept ({acceptedIds.size})
                </Button>
              </div>
            </div>
          ) : isAnimating ? (
            <div className="flex flex-col h-full" aria-busy="true">
              <div
                role="button"
                tabIndex={0}
                title="Click to edit"
                onMouseDown={handleSkipAnimation}
                onFocus={handleSkipAnimation}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    handleSkipAnimation()
                  }
                }}
                className={`${TEXTAREA_STYLES} whitespace-pre-wrap cursor-text`}
                aria-label="Loading metric prompt, click to edit"
              >
                {visibleText}
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <MetricPromptMentionInput
                textAreaRef={textAreaRef}
                className={`${TEXTAREA_STYLES} ${isOverLimit ? "text-coval-danger-text" : ""}`}
                value={displayPrompt}
                onChange={handleDisplayPromptChange}
                placeholder={placeholder}
                sections={mentionSections}
              />
              <div
                className={`flex justify-end mt-2 text-xs ${
                  isOverLimit
                    ? "text-coval-danger-text font-medium"
                    : "text-coval-text-muted"
                }`}
              >
                {isOverLimit && (
                  <span className="mr-2">
                    Prompt exceeds {MAX_PROMPT_CHARACTERS.toLocaleString()}{" "}
                    character limit
                  </span>
                )}
                <span>
                  {characterCount.toLocaleString()} /{" "}
                  {MAX_PROMPT_CHARACTERS.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Context Preview Modal for annotation selection */}
      <ContextPreviewModal
        isOpen={showContextPreview}
        onClose={handleContextPreviewClose}
        onConfirm={(config) => {
          void handleContextConfirm(config)
        }}
        currentPrompt={currentPrompt}
        metricType={metricType as MetricManager}
        annotations={relatedAnnotations}
        metricOutputs={metricOutputs}
        metric={metricRecordData}
        isLoading={isGenerating}
        errorMessage={optimizationError}
        currentUserId={user?.id}
        totalAnnotationCount={allAnnotations.length}
        feedbackUnavailableReason={feedbackUnavailableReason}
      />
    </>
  )
}
